import React, { useEffect, useState } from "react";
import Dashboard from "../../layouts/Dashboard";
import ListEditor from "../../components/ListEditor";
import styled from "styled-components";
import axios from "../../services/axios";
import LoadingArea from "../../components/LoadingArea";
import RSelect from "react-select";
import saveLog from "../../services/saveLog";
import { Card } from "antd";
import isConsultant from "../../services/isConsultant";
import useRole from "../../hooks/useRole";
import { checkPermission } from "../../utils/checkPermission";

const CardContainer = styled.div`
  margin-top: ${(props) => (props.isFirst ? "0" : "2rem")};
`;

function SettingsDropdownChoices({
  rejectionReasons,
  contactSources,
  setRejectionReasons,
  setContactSources,
  queryTypes,
  setQueryTypes,
  canAddRejectionReason,
  canDeleteRejectionReason,
  canAddContactSource,
  canDeleteContactSource,
  canAddQueryType,
  canDeleteQueryType,
}) {
  async function onClickSave() {
    try {
      saveLog("SETTINGS_EDIT", `Updated Dropdown Menu Choices (Autosave)`);
      await axios.patch(
        "/config",
        {
          rejectionReasons,
          contactSources,
          queryTypes,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("user.token")}`,
          },
        }
      );
    } catch (e) {
      console.error(e);
    }
  }

  useEffect(() => {
    onClickSave();
  }, [rejectionReasons, contactSources, queryTypes]);

  return (
    <div>
    {!isConsultant() && <Card title="Drop Downs Menu Choices" className="mb-3">
      <div>
        <h6>Reasons for Rejection</h6>
        <ListEditor
          onUpdate={onClickSave}
          items={rejectionReasons}
          setItems={setRejectionReasons}
          canAdd={canAddRejectionReason}
          canDelete={canDeleteRejectionReason}
        />

        <h6 className="mt-4">Source for contacts</h6>
        <ListEditor
          onUpdate={onClickSave}
          items={contactSources}
          setItems={setContactSources}
          canAdd={canAddContactSource}
          canDelete={canDeleteContactSource}
        />

        <h6 className="mt-4">Query Types</h6>
        <ListEditor
          onUpdate={onClickSave}
          items={queryTypes}
          setItems={setQueryTypes}
          canAdd={canAddQueryType}
          canDelete={canDeleteQueryType}
        />
      </div>
      {/* <div className="card-footer">
        <button className="btn btn-primary" onClick={onClickSave}>
          Save
        </button>
      </div> */}
    </Card>}
    </div>
  );
}

function SettingsLoanProviders({
  loanProviders,
  setLoanProviders,
  canAdd,
  canDelete,
  canSave,
}) {
  async function onClickSave() {
    try {
      await axios.patch(
        "/config",
        {
          loanProviders,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("user.token")}`,
          },
        }
      );
    } catch (e) {
      console.error(e);
    }
  }

  return (
    <div>
    {!isConsultant() && <Card title="Loan Providers">
      <div>
        <p>
          Please enter emails of loan providers. They will be notified once
          about the candidates who choose to share their information.
        </p>
        <ListEditor
          items={loanProviders}
          setItems={setLoanProviders}
          canAdd={canAdd}
          canDelete={canDelete}
        />
      </div>
      <div className="mt-3">
        {canSave && (
          <button className="btn btn-primary" onClick={onClickSave}>
            Save
          </button>
        )}
      </div>
    </Card>}
    </div>
  );
}

function SettingsReminders({ reminder, setReminder, canSave }) {
  // Stateful Hooks
  const options = [
    { label: "Every Day", value: 1 },
    { label: "Every 2 Days", value: 2 },
    { label: "Every 4 Days", value: 4 },
  ];

  // Effects and Events

  async function onClickSave() {
    try {
      await axios.patch(
        "/config",
        {
          reminder,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("user.token")}`,
          },
        }
      );
    } catch (e) {
      console.error(e);
    }
  }

  return (
    <div>
    {!isConsultant() && <Card title="Reminder Settings">
      <div>
        <label htmlFor="frequency">Email Reminder Frequency</label>
        <RSelect
          id="frequency"
          options={options}
          value={reminder}
          onChange={(selected) => {
            setReminder(selected);
          }}
        />
      </div>
      <div className="mt-3">
        {canSave && (
          <button className="btn btn-primary" onClick={onClickSave}>
            Save
          </button>
        )}
      </div>
    </Card>}
    </div>
  );
}

function SettingsAdmin() {
  const [rejectionReasons, setRejectionReasons] = useState([]);
  const [contactSources, setContactSources] = useState([]);
  const [loanProviders, setLoanProviders] = useState([]);
  const [queryTypes, setQueryTypes] = useState([]);
  const [reminder, setReminder] = useState();
  const [loading, setLoading] = useState(true);
  const [canViewSettings, setCanViewSettings] = useState(false);
  const [canAddRejectionReason, setCanAddRejectionReason] = useState(false);
  const [canDeleteRejectionReason, setCanDeleteRejectionReason] = useState(false);
  const [canAddContactSource, setCanAddContactSource] = useState(false);
  const [canDeleteContactSource, setCanDeleteContactSource] = useState(false);
  const [canAddQueryType, setCanAddQueryType] = useState(false);
  const [canDeleteQueryType, setCanDeleteQueryType] = useState(false);
  const [canAddLoanProvider, setCanAddLoanProvider] = useState(false);
  const [canDeleteLoanProvider, setCanDeleteLoanProvider] = useState(false);
  const [canSaveLoanProviders, setCanSaveLoanProviders] = useState(false);
  const [canSaveReminders, setCanSaveReminders] = useState(false);
  const userRole = useRole();

  const checkPermissions = async () => {
    try {
      const [
        viewAllowed,
        addRejectionReason,
        deleteRejectionReason,
        addContactSource,
        deleteContactSource,
        addQueryType,
        deleteQueryType,
        addLoanProvider,
        deleteLoanProvider,
        saveLoanProviders,
        saveReminders,
      ] = await Promise.all([
        checkPermission(userRole, "settings_module", "view_settings"),
        checkPermission(userRole, "settings_module", "reasons_for_rejection_add"),
        checkPermission(userRole, "settings_module", "reasons_for_rejection_delete"),
        checkPermission(userRole, "settings_module", "source_for_contacts_add"),
        checkPermission(userRole, "settings_module", "source_for_contacts_delete"),
        checkPermission(userRole, "settings_module", "query_types_add"),
        checkPermission(userRole, "settings_module", "query_types_delete"),
        checkPermission(userRole, "settings_module", "loan_providers_add"),
        checkPermission(userRole, "settings_module", "loan_providers_delete"),
        checkPermission(userRole, "settings_module", "loan_providers_save"),
        checkPermission(userRole, "settings_module", "reminder_settings_save"),
      ]);

      setCanViewSettings(viewAllowed);
      setCanAddRejectionReason(addRejectionReason);
      setCanDeleteRejectionReason(deleteRejectionReason);
      setCanAddContactSource(addContactSource);
      setCanDeleteContactSource(deleteContactSource);
      setCanAddQueryType(addQueryType);
      setCanDeleteQueryType(deleteQueryType);
      setCanAddLoanProvider(addLoanProvider);
      setCanDeleteLoanProvider(deleteLoanProvider);
      setCanSaveLoanProviders(saveLoanProviders);
      setCanSaveReminders(saveReminders);
    } catch (error) {
      console.error("Permission check failed for Settings Module:", error);
    }
  };

  async function loadConfig() {
    try {
      const { data } = await axios.get("/config", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("user.token")}`,
        },
      });
      setRejectionReasons(data.rejectionReasons || []);
      setContactSources(data.contactSources || []);
      setLoanProviders(data.loanProviders || []);
      setQueryTypes(data.queryTypes || []);
      setReminder(data.reminder);
      setLoading(false);
    } catch (e) {
      console.error(e);
    }
  }

  useEffect(() => {
    loadConfig();
  }, []);

  useEffect(() => {
    if (userRole) {
      checkPermissions();
    }
  }, [userRole]);

  if (!canViewSettings) {
    return <div></div>;
  }

  return (
    <div>
    {!isConsultant() && <Dashboard title="Admin Settings">
      <LoadingArea isLoading={loading}>
        <div className="row">
          <div className="col-md-6">
            <CardContainer isFirst>
              <SettingsDropdownChoices
                rejectionReasons={rejectionReasons}
                contactSources={contactSources}
                setRejectionReasons={setRejectionReasons}
                setContactSources={setContactSources}
                queryTypes={queryTypes}
                setQueryTypes={setQueryTypes}
                canAddRejectionReason={canAddRejectionReason}
                canDeleteRejectionReason={canDeleteRejectionReason}
                canAddContactSource={canAddContactSource}
                canDeleteContactSource={canDeleteContactSource}
                canAddQueryType={canAddQueryType}
                canDeleteQueryType={canDeleteQueryType}
              />
            </CardContainer>
          </div>
          <div className="col-md-6">
            <CardContainer isFirst>
              <SettingsLoanProviders
                loanProviders={loanProviders}
                setLoanProviders={setLoanProviders}
                canAdd={canAddLoanProvider}
                canDelete={canDeleteLoanProvider}
                canSave={canSaveLoanProviders}
              />
            </CardContainer>
            <CardContainer>
              <SettingsReminders
                reminder={reminder}
                setReminder={setReminder}
                canSave={canSaveReminders}
              />
            </CardContainer>
          </div>
        </div>
      </LoadingArea>
    </Dashboard>} 
    </div>
  );
}

export default SettingsAdmin;
